import React from "react";
import { useHistory } from "react-router-dom";
import WebsiteLayout from "../components/layouts/WebsiteLayout";
import { IonIcon } from "@ionic/react";
import {
  checkmarkCircle,
  shieldCheckmark,
  notifications,
  peopleCircle,
  phonePortrait,
  logoApple,
  logoGooglePlaystore,
  listCircle,
  chatbox,
  chatboxOutline,
  chatboxEllipsesOutline,
  chatboxEllipses,
  chatbubble,
  chatbubblesOutline,
  chatbubbles,
} from "ionicons/icons";

const LandingPage: React.FC = () => {
  const history = useHistory();

  const appStoreUrl = "https://apps.apple.com/us/app/werkapp/id6446168426";
  const googlePlayUrl =
    "https://play.google.com/store/apps/details?id=com.pipedream.werkapp&hl=en_US";

  const steps = [
    ["Invite your people", "Build your network of werkers and organize them into the crews you actually use."],
    ["Create the job", "Add the date, time, location, pay, instructions, and number of openings."],
    ["Send it out", "Notify the right werkers at the same time and let them claim open slots directly in the app."],
    ["See it fill", "Know who is working, what is still open, and where every job stands without chasing replies."],
    ["Keep communication attached", "Changes, instructions, and job-specific conversation stay with the job."],
    ["Track it through completion", "Follow every detail from open to filled, completed, paid, and done."],
  ];

  const features = [
    [notifications, "Notify everyone at once", "The right werkers see the opportunity without you copying, pasting, and repeating yourself."],
    [checkmarkCircle, "Let open work fill itself", "Werkers claim available slots directly while you keep a clear view of what remains open."],
    [listCircle, "Organize crews", "Send different kinds of work to the different groups you actually schedule."],
    [chatbubbles, "Keep job communication together", "Stop digging through unrelated messages to find the instruction or change that matters."],
    [phonePortrait, "Work from phone", "Werkers claim shifts on the go while schedulers manage the jobs all from mobile."],
    [peopleCircle, "Start with the team you have", "Real werkers and Dummy Werkers can exist in the same schedule, so adoption happens at your pace."],
  ];

  return (
    <WebsiteLayout>
      <style>
        {`
          .landing-page * { box-sizing: border-box; }
          .landing-page section { padding: 5rem 2rem; }
          .landing-container { width: 100%; max-width: 1120px; margin: 0 auto; }
          .landing-narrow { width: 100%; max-width: 820px; margin: 0 auto; }
          .hero-actions, .download-actions { display: flex; justify-content: center; gap: 1rem; flex-wrap: wrap; }
          .primary-cta, .secondary-cta, .store-button {
            border-radius: 10px; cursor: pointer; font-family: inherit; font-weight: 700;
            transition: transform .2s ease, box-shadow .2s ease;
          }
          .primary-cta:hover, .secondary-cta:hover, .store-button:hover { transform: translateY(-2px); }
          .primary-cta { border: 0; padding: 1rem 1.5rem; background: #f4b41a; color: #1a1a1a; font-size: 1.05rem; }
          .secondary-cta { padding: 1rem 1.5rem; background: transparent; color: #fff; border: 2px solid rgba(255,255,255,.35); font-size: 1.05rem; }
          .store-button { border: 0; padding: 1rem 1.5rem; background: #1a1a1a; color: #fff; font-size: 1.05rem; display: inline-flex; align-items: center; justify-content: center; gap: .65rem; min-width: 180px; }
          .eyebrow { display: inline-block; margin-bottom: 1rem; color: #f4b41a; font-size: .95rem; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
          .section-heading { margin: 0 0 1rem; color: #1a1a1a; font-size: clamp(1.9rem,4vw,2.7rem); line-height: 1.15; font-weight: 800; text-align: center; }
          .section-intro { max-width: 760px; margin: 0 auto 3rem; color: #666; font-size: 1.1rem; line-height: 1.75; text-align: center; }
          .step-grid, .feature-grid, .video-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 1.5rem; }
          .step-card { padding: 2rem; background: #fff; border: 1px solid #e7e7e7; border-radius: 16px; box-shadow: 0 8px 28px rgba(0,0,0,.06); }
          .step-number { width: 42px; height: 42px; margin-bottom: 1.25rem; border-radius: 50%; background: #f4b41a; color: #1a1a1a; display: flex; align-items: center; justify-content: center; font-weight: 900; }
          .feature-card { text-align: center; padding: 1.75rem; }
          .feature-icon { width: 76px; height: 76px; margin: 0 auto 1.25rem; border-radius: 50%; background: #f4b41a; color: #1a1a1a; display: flex; align-items: center; justify-content: center; }
          .dummy-layout { display: grid; grid-template-columns: minmax(0,.95fr) minmax(0,1.05fr); gap: 3rem; align-items: center; }
          .dummy-panel { background: linear-gradient(135deg,#1a1a1a 0%,#303030 100%); border-radius: 18px; padding: 2.25rem; color: #fff; box-shadow: 0 14px 36px rgba(0,0,0,.18); }
          .dummy-row { display: flex; align-items: center; gap: 1rem; padding: 1rem; margin-top: 1rem; border-radius: 12px; background: rgba(255,255,255,.08); }
          .dummy-avatar { width: 46px; height: 46px; flex: 0 0 46px; border-radius: 50%; background: #f4b41a; color: #1a1a1a; display: flex; align-items: center; justify-content: center; font-weight: 900; font-size: 1.25rem; }
          .video-frame { width: 100%; aspect-ratio: 16/9; border: 0; border-radius: 14px; box-shadow: 0 8px 24px rgba(0,0,0,.12); }
          @media (max-width: 900px) { .step-grid,.feature-grid,.video-grid { grid-template-columns: 1fr 1fr; } .dummy-layout { grid-template-columns: 1fr; } }
          @media (max-width: 680px) {
            .landing-page section { padding: 4rem 1.25rem; }
            .step-grid,.feature-grid,.video-grid { grid-template-columns: 1fr; }
            .hero-actions,.download-actions { flex-direction: column; align-items: stretch; }
            .primary-cta,.secondary-cta,.store-button { width: 100%; }
          }
        `}
      </style>

      <main className="landing-page">
        <section style={{ background: "linear-gradient(135deg,#171717 0%,#2d2d2d 100%)", color: "#fff", textAlign: "center" }}>
          <div className="landing-narrow">
            <span className="eyebrow">Built for schedulers</span>
            <h1 style={{ margin: "0 0 1.5rem", fontSize: "clamp(2.35rem,6vw,4.25rem)", lineHeight: 1.08, fontWeight: 900 }}>
              Schedule the work.<br />Fill the slots.<br />Skip the group-text chaos.
            </h1>
            <p style={{ maxWidth: 760, margin: "0 auto 1rem", color: "#f4b41a", fontSize: "clamp(1.15rem,2.5vw,1.45rem)", lineHeight: 1.5, fontWeight: 700 }}>
              WerkApp gives off-duty schedulers one place to organize their people, post jobs, fill openings, communicate changes, and track every detail.
            </p>
            <p style={{ maxWidth: 720, margin: "0 auto 2.25rem", color: "#d0d0d0", fontSize: "1.08rem", lineHeight: 1.75 }}>
              Schedulers pay. Werkers use it free. And with Dummy Werkers, nobody has to hold up your move into the app.
            </p>
            <div className="hero-actions">
              <button type="button" className="primary-cta" onClick={() => window.open(appStoreUrl, "_blank", "noopener,noreferrer")}>Download for iPhone</button>
              <button type="button" className="secondary-cta" onClick={() => window.open(googlePlayUrl, "_blank", "noopener,noreferrer")}>Download for Android</button>
            </div>
            <p style={{ margin: "1.25rem 0 0", color: "#2dd36f", fontSize: "1.25rem" }}>Werkers are free!</p>
          </div>
        </section>

        <section style={{ backgroundColor: "#f7f7f7" }}>
          <div className="landing-container">
            <h2 className="section-heading">Everything starts with your people.</h2>
            <p className="section-intro">Bring your werkers into WerkApp once, then use that same network every time you need to schedule a job. No rebuilding contact lists. No starting another group text.</p>
            <div className="step-grid">
              {steps.map(([title, text], index) => (
                <article className="step-card" key={title}>
                  <div className="step-number">{index + 1}</div>
                  <h3 style={{ margin: "0 0 .75rem", color: "#1a1a1a", fontSize: "1.35rem" }}>{title}</h3>
                  <p style={{ margin: 0, color: "#666", lineHeight: 1.7 }}>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section style={{ backgroundColor: "#fff" }}>
          <div className="landing-container dummy-layout">
            <div>
              <span className="eyebrow">Start before everyone is ready</span>
              <h2 style={{ margin: "0 0 1.25rem", color: "#1a1a1a", fontSize: "clamp(2rem,4vw,3rem)", lineHeight: 1.15, fontWeight: 850 }}>
                One holdout should not force you to run two systems.
              </h2>
              <p style={{ margin: "0 0 1.25rem", color: "#666", fontSize: "1.1rem", lineHeight: 1.75 }}>
                Getting your people into WerkApp is <strong>Easy</strong> and <strong>FREE</strong>. But just in case some of your people aren't in the app yet, Dummy Werkers let you keep scheduling without missing a beat.

              </p>
              <p style={{ margin: 0, color: "#1a1a1a", fontSize: "1.08rem", lineHeight: 1.7, fontWeight: 700 }}>
                Use a temporary placeholder, or save a named Dummy Werker for someone you regularly schedule outside the app.
              </p>
            </div>
            <div className="dummy-panel">
              <p style={{ margin: "0 0 .35rem", color: "#f4b41a", fontSize: ".95rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".08em" }}>Example With Dummies:</p>
              <h3 style={{ margin: "0 0 1.5rem", fontSize: "1.65rem" }}>One job with 3 slots</h3>
              {[['JD','Jordan Davis','Actual WerkApp user (way better)'],['?','Dummy Werker','Placeholder for someone outside the app'],['?','John Doe','Saved named Dummy Werker']].map(([initials,name,status]) => (
                <div className="dummy-row" key={name}>
                  <div className="dummy-avatar">{initials}</div>
                  <div><strong style={{ display: "block", marginBottom: ".2rem" }}>{name}</strong><span style={{ color: "#bdbdbd", fontSize: ".95rem" }}>{status}</span></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section style={{ backgroundColor: "#f7f7f7" }}>
          <div className="landing-container">
            <h2 className="section-heading">See WerkApp in action.</h2>
            <p className="section-intro">Get a quick look at the workflow, then download the app and run a real job on it.</p>
            <div className="video-grid">
              <iframe src="https://www.youtube.com/embed/31dD458TGbQ" title="WerkApp overview video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="video-frame" />
              <iframe src="https://www.youtube.com/embed/INgR7o0QUnI" title="WerkApp scheduling video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="video-frame" />
              <iframe src="https://www.youtube.com/embed/1TKHmtAbBdA" title="WerkApp feature video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="video-frame" />
            </div>
          </div>
        </section>

        <section style={{ backgroundColor: "#fff" }}>
          <div className="landing-container">
            <h2 className="section-heading">Built around the scheduler&apos;s real workflow.</h2>
            <p className="section-intro">WerkApp stays focused on the things that make recurring details easier to organize and easier to repeat.</p>
            <div className="feature-grid">
              {features.map(([icon, title, text]) => (
                <article className="feature-card" key={String(title)}>
                  <div className="feature-icon"><IonIcon icon={icon as string} style={{ fontSize: "2.45rem" }} /></div>
                  <h3 style={{ margin: "0 0 .75rem", color: "#1a1a1a", fontSize: "1.3rem" }}>{title}</h3>
                  <p style={{ margin: 0, color: "#666", lineHeight: 1.7 }}>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section style={{ background: "linear-gradient(135deg,#1a1a1a 0%,#303030 100%)", color: "#fff", textAlign: "center" }}>
          <div className="landing-narrow">
            <span className="eyebrow">Simple on purpose</span>
            <h2 style={{ margin: "0 0 1.25rem", fontSize: "clamp(2rem,4vw,3rem)", lineHeight: 1.15, fontWeight: 850 }}>
              Your scheduling system should be easier than the work you are scheduling.
            </h2>
            <p style={{ margin: "0 auto", maxWidth: 700, color: "#d0d0d0", fontSize: "1.12rem", lineHeight: 1.75 }}>
              Create the job, fill the openings, keep everyone informed, and move on.
            </p>
          </div>
        </section>

        <section style={{ backgroundColor: "#f5f5f5", textAlign: "center" }}>
          <div className="landing-narrow">
            <h2 className="section-heading">Run your next job on WerkApp.</h2>
            <p className="section-intro" style={{ marginBottom: "2rem" }}>Download the app, invite your people, and see how much easier the next schedule can be.</p>
            <div className="download-actions">
              <button type="button" className="store-button" onClick={() => window.open(appStoreUrl, "_blank", "noopener,noreferrer")}><IonIcon icon={logoApple} style={{ fontSize: "1.5rem" }} />App Store</button>
              <button type="button" className="store-button" onClick={() => window.open(googlePlayUrl, "_blank", "noopener,noreferrer")}><IonIcon icon={logoGooglePlaystore} style={{ fontSize: "1.5rem" }} />Google Play</button>
            </div>
            <p style={{ margin: "1.5rem 0 0", color: "#888", fontSize: ".95rem" }}>Schedulers can also manage everything from the web portal.</p>
          </div>
        </section>

        <section style={{ background: "linear-gradient(135deg,#f4b41a 0%,#d89f15 100%)", textAlign: "center" }}>
          <div className="landing-narrow">
            <h2 style={{ margin: "0 0 1rem", color: "#1a1a1a", fontSize: "clamp(2rem,4vw,3rem)", lineHeight: 1.15, fontWeight: 900 }}>Schedulers, look what we built for you.</h2>
            <p style={{ maxWidth: 670, margin: "0 auto 2rem", color: "#1a1a1a", fontSize: "1.18rem", lineHeight: 1.7, fontWeight: 600 }}>
              Bring your people. Post the work. Fill the slots. Keep the whole detail organized in one place.
            </p>
            <button type="button" className="store-button" onClick={() => window.open(appStoreUrl, "_blank", "noopener,noreferrer")}>Get WerkApp</button>

            {/* Portal access can be restored later.
            <div style={{ marginTop: "1.5rem" }}>
              <button type="button" onClick={() => history.push("/portal")}>Already have an account? Access Portal →</button>
            </div>
            */}
          </div>
        </section>
      </main>
    </WebsiteLayout>
  );
};

export default LandingPage;